import React, { useState, useEffect, useCallback } from 'react';
import { Search, User, Mail, Trash2, Edit } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog } from '../../components/Modal';
import EmptyState from '../../components/EmptyState';

const AdminProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const getErrorMessage = useCallback((error, fallbackMessage) => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.code === 'ERR_NETWORK') {
      return 'Cannot reach the server. Please check your connection.';
    }
    return error?.message || fallbackMessage;
  }, []);

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/profiles', {
        params: { page, limit: 20, search },
      });
      if (response.data.success) {
        setProfiles(response.data.profiles);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load profiles'));
    } finally {
      setLoading(false);
    }
  }, [page, search, getErrorMessage]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleDelete = async (profileId) => {
    try {
      setDeleting(true);
      const response = await api.delete(`/admin/profiles/${profileId}`);
      if (response.data.success) {
        toast.success('Profile deleted successfully');
        fetchProfiles();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete profile'));
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (profile) => {
    setSelectedProfile({
      ...profile,
      age: profile.age || '',
      gender: profile.gender || '',
      country: profile.country || '',
      language: profile.language || 'english',
      tone: profile.tone || 'entertaining',
      ending: profile.ending || 'happy',
      voice: profile.voice || 'male',
      reading_speed: profile.reading_speed || 'normal',
    });
    setShowEditModal(true);
  };

  const handleUpdateProfile = async () => {
    if (!selectedProfile) return;
    try {
      setSaving(true);
      const response = await api.put(`/admin/profiles/${selectedProfile.id}`, {
        firstName: selectedProfile.first_name?.trim(),
        lastName: selectedProfile.last_name?.trim(),
        age: selectedProfile.age ? Number(selectedProfile.age) : null,
        gender: selectedProfile.gender,
        country: selectedProfile.country?.trim(),
        language: selectedProfile.language,
        tone: selectedProfile.tone,
        ending: selectedProfile.ending,
        voice: selectedProfile.voice,
        readingSpeed: selectedProfile.reading_speed,
      });
      if (response.data.success) {
        toast.success('Profile updated successfully');
        setShowEditModal(false);
        fetchProfiles();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (profile) => {
    setDeleteTarget(profile);
    setShowConfirmModal(true);
  };

  return (
    <AdminSidebar>
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Profile Management</h1>
          <p className="text-gray-600">Manage all child profiles</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search profiles by name or user email..."
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : profiles.length === 0 ? (
          <EmptyState
            icon={<User className="h-7 w-7" />}
            title={
              search
                ? `No profiles found for “${search}”`
                : 'No profiles yet'
            }
            description={
              search
                ? 'Try refining or clearing your search to view all profiles.'
                : 'As families create child profiles, they will appear here for review and management.'
            }
            action={
              search
                ? {
                    label: 'Clear search',
                    onClick: () => {
                      setSearch('');
                      setPage(1);
                    },
                  }
                : undefined
            }
          />
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Profile</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell">User</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden sm:table-cell">Age</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden sm:table-cell">Stories</th>
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell">Created</th>
                      <th className="px-4 sm:px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {profiles.map((profile) => (
                      <tr key={profile.id} className="hover:bg-gradient-to-r hover:from-purple-50/50 hover:to-pink-50/50 transition-all duration-150 group">
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 sm:h-12 sm:w-12">
                              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-md ring-2 ring-white">
                                {profile.first_name?.[0]?.toUpperCase() || 'P'}
                              </div>
                            </div>
                            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                              <div className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                                {profile.first_name} {profile.last_name}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500 capitalize truncate">
                                {profile.gender} {profile.country && `• ${profile.country}`}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden md:table-cell">
                          <div className="text-sm font-medium text-gray-900">{profile.user_first_name}</div>
                          <div className="text-xs text-gray-500 flex items-center truncate">
                            <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{profile.user_email}</span>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-100 text-blue-700 text-sm font-medium">
                            {profile.age || 'N/A'} {profile.age && 'years'}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-pink-100 text-pink-700 text-sm font-medium">
                            {profile.story_count || 0}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden lg:table-cell">
                          <div className="text-sm text-gray-600">
                            {new Date(profile.created_at).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => openEditModal(profile)}
                              className="p-2 text-purple-600 hover:text-white hover:bg-purple-600 rounded-lg transition-all duration-200 group-hover:scale-110"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                            <button
                              onClick={() => openDeleteConfirm(profile)}
                              className="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-all duration-200 group-hover:scale-110"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {pagination.pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing page {pagination.page} of {pagination.pages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === pagination.pages}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

      {/* Edit Profile Modal */}
      {showEditModal && selectedProfile && (
        <Modal title="Edit Profile" onClose={() => setShowEditModal(false)} width="max-w-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                value={selectedProfile.first_name}
                onChange={(e) =>
                  setSelectedProfile((prev) => ({ ...prev, first_name: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                value={selectedProfile.last_name || ''}
                onChange={(e) =>
                  setSelectedProfile((prev) => ({ ...prev, last_name: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
              <input
                type="number"
                min="2"
                max="18"
                value={selectedProfile.age}
                onChange={(e) =>
                  setSelectedProfile((prev) => ({ ...prev, age: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select
                value={selectedProfile.gender}
                onChange={(e) =>
                  setSelectedProfile((prev) => ({ ...prev, gender: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              >
                <option value="">Select gender</option>
                <option value="boy">Boy</option>
                <option value="girl">Girl</option>
                <option value="non-binary">Non-binary</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input
                type="text"
                value={selectedProfile.country || ''}
                onChange={(e) =>
                  setSelectedProfile((prev) => ({ ...prev, country: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
              <select
                value={selectedProfile.language}
                onChange={(e) =>
                  setSelectedProfile((prev) => ({ ...prev, language: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              >
                <option value="english">English</option>
                <option value="spanish">Spanish</option>
                <option value="french">French</option>
                <option value="german">German</option>
                <option value="italian">Italian</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Story Tone</label>
              <select
                value={selectedProfile.tone}
                onChange={(e) =>
                  setSelectedProfile((prev) => ({ ...prev, tone: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              >
                <option value="entertaining">Entertaining</option>
                <option value="humorous">Humorous</option>
                <option value="adventurous">Adventurous</option>
                <option value="inspirational">Inspirational</option>
                <option value="educational">Educational</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Story Ending</label>
              <select
                value={selectedProfile.ending}
                onChange={(e) =>
                  setSelectedProfile((prev) => ({ ...prev, ending: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              >
                <option value="happy">Happy</option>
                <option value="open-ended">Open-ended</option>
                <option value="surprise-twist">Surprise Twist</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Narrator Voice</label>
              <select
                value={selectedProfile.voice}
                onChange={(e) =>
                  setSelectedProfile((prev) => ({ ...prev, voice: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reading Speed</label>
              <select
                value={selectedProfile.reading_speed}
                onChange={(e) =>
                  setSelectedProfile((prev) => ({ ...prev, reading_speed: e.target.value }))
                }
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              >
                <option value="slower">Slower</option>
                <option value="normal">Normal</option>
                <option value="faster">Faster</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-6">
            <button
              onClick={() => setShowEditModal(false)}
              className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateProfile}
              disabled={saving}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {showConfirmModal && deleteTarget && (
        <ConfirmDialog
          title="Delete Profile"
          description={`Are you sure you want to delete ${deleteTarget.first_name}'s profile? This will remove all related stories.`}
          confirmLabel={deleting ? 'Deleting...' : 'Yes, Delete'}
          onConfirm={() => {
            setShowConfirmModal(false);
            handleDelete(deleteTarget.id);
          }}
          onCancel={() => {
            setShowConfirmModal(false);
            setDeleteTarget(null);
          }}
        />
      )}
    </AdminSidebar>
  );
};

export default AdminProfiles;

